import {
    Button,
    Flex,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import type { TagType } from '@modules/product-tagging/type';
import type { Image } from '~/modules/page-builder/type/message-passing';
import ProductTaggingShell from '~/modules/product-tagging/components/product-tagging-shell/ProductTaggingShell';
import { useProductTagging } from '~/modules/product-tagging/context';
import TagPopover from '../tag-popover/TagPopover';
import * as S from './DetailSingleImageShell.style';
import HoverButtonsImage from './HoverButtonsImage';
import ImageCaption from '../../common/image-caption/ImageCaption';
import type { ColumnDimensionType } from '@modules/page-builder/types';
import { ExternalLinkLine } from '@archipro-design/icons';
import ProductTagsDisplay from '@modules/product-tagging/components/product-tags-display/ProductTagsDisplay';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';
import { useState } from 'react';
import { useHydrated } from 'remix-utils';

interface DetailSingleImageShellProps {
    children?: React.ReactNode;
    caption?: string;
    size?: string;
    className?: string;
    libraryImage?: Image;
    dimension: ColumnDimensionType;
    taggingEnabled?: boolean;
    tags?: TagType[];
    uniqueApprovedTags?: TagType[];
    imageURL?: string;
    isProduct?: boolean;
}

const DetailSingleImageShell = ({
    children,
    caption,
    className,
    libraryImage,
    dimension,
    taggingEnabled = true,
    tags,
    uniqueApprovedTags,
    imageURL,
    isProduct = false,
}: DetailSingleImageShellProps) => {
    const {
        state: { isTagging },
        canCreateTag,
    } = useProductTagging();
    const isLinked = !!libraryImage?.ClickableLink || isProduct;
    const { css } = useStyles({
        dimension,
        isTagging,
        isLinked,
    });

    const { mobile } = useAppDisplayModeContext();

    const userCanTag = taggingEnabled && canCreateTag;

    // Dots are desktop-only. On mobile the under-image "Products tagged (N)"
    // control opens the bottom drawer — pins on the photo steal that tap.
    const showTagsOnImage =
        useABTestIsOn('product_tagging_dots') && !canCreateTag && !mobile;

    const displayTags =
        mobile && uniqueApprovedTags && uniqueApprovedTags?.length > 0;

    // Only render interactive components after hydration (SSR-safe)
    const isHydrated = useHydrated();

    const onClickLinkedImage = () => {
        if (!isHydrated || !libraryImage?.ClickableLink || isTagging) {
            return;
        }
        window.open(libraryImage.ClickableLink, '_blank');
    };

    const [showHoverButtons, setShowHoverButtons] = useState(true);

    return (
        <>
            <div
                className={`${css(S.DetailSingleImageContainer)} ${className || ''}`}
                onClick={onClickLinkedImage}
            >
                {children}
                {isHydrated && (
                    <>
                        {taggingEnabled &&
                            libraryImage?.ID &&
                            (!mobile || userCanTag) && (
                                <ProductTaggingShell
                                    libraryImage={libraryImage}
                                    tags={tags}
                                />
                            )}

                        {showTagsOnImage &&
                            libraryImage &&
                            tags &&
                            tags.length > 0 && (
                                <ProductTagsDisplay
                                    libraryImage={libraryImage}
                                    tags={tags}
                                    onToggle={(toggled) =>
                                        setShowHoverButtons(!toggled)
                                    }
                                />
                            )}

                        {libraryImage?.ID && showHoverButtons && imageURL && (
                            <HoverButtonsImage
                                photoID={libraryImage.ID}
                                libraryItemId={
                                    libraryImage.LibraryItemID ?? undefined
                                }
                                imageURL={imageURL}
                                downloadLink={
                                    libraryImage?.DownloadLink || undefined
                                }
                                taggingEnabled={userCanTag}
                            />
                        )}
                    </>
                )}
                {libraryImage?.ClickableLink && (
                    <div className={css(S.DetailSingleImageLinkedIcon)}>
                        <Button
                            icon={<ExternalLinkLine />}
                            size={24}
                            color="grey"
                            as="a"
                            href={libraryImage?.ClickableLink}
                            target="_blank"
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        />
                    </div>
                )}
            </div>
            {(caption || displayTags) && (
                <div className={css(S.MobileCaptionContainer)}>
                    {displayTags && (
                        <Flex>
                            <TagPopover
                                tags={uniqueApprovedTags}
                                dimension={dimension}
                            />
                        </Flex>
                    )}
                    {caption && (
                        <div className={css(S.ImageCaptionContainer)}>
                            <ImageCaption text={caption} isMain={false} />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default DetailSingleImageShell;
